import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import SearchableSelect from "@/components/SearchableSelect";
import { toast } from "sonner";
import { UserCheck, Save, Stethoscope, Building2, BadgeInfo, PenLine, Upload, Trash2, Loader2, Globe, Users, CheckCircle2, XCircle, Share2, CreditCard, CalendarClock, Video, IndianRupee, Clock, Landmark } from "lucide-react";

const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30 focus:border-[#1E5AA8]";
const PLAN_LABEL = { monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly", trial: "Free trial" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");
const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];

export default function DoctorProfile() {
  const { user, refresh } = useAuth();
  const { setLanguage } = useI18n();
  const [specialties, setSpecialties] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [form, setForm] = useState({
    full_name: "", qualifications: "", registration_number: "", specialty: "",
    clinic_name: "", clinic_address: "", clinic_contact: "",
    contact_phone: "", contact_whatsapp: "", contact_email: "",
    country: "India", ui_language: "English",
  });
  const [saving, setSaving] = useState(false);
  const [team, setTeam] = useState([]);
  const [sub, setSub] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [tele, setTele] = useState({ teleconsult_enabled: false, consultation_fee: "", upi_id: "", bank_holder: "", bank_account_number: "", bank_ifsc: "", bank_name: "", slot_minutes: 15 });
  const [avail, setAvail] = useState({});
  const [savingTele, setSavingTele] = useState(false);

  const setDay = (d, patch) => setAvail((a) => ({ ...a, [d]: { enabled: false, start: "18:00", end: "21:00", ...(a[d] || {}), ...patch } }));

  const saveTele = async () => {
    setSavingTele(true);
    try {
      await api.put("/profile", {
        teleconsult_enabled: tele.teleconsult_enabled,
        consultation_fee: Number(tele.consultation_fee) || 0,
        upi_id: tele.upi_id, bank_holder: tele.bank_holder,
        bank_account_number: tele.bank_account_number, bank_ifsc: tele.bank_ifsc, bank_name: tele.bank_name,
        slot_minutes: Number(tele.slot_minutes) || 15, availability: avail,
      });
      await refresh();
      toast.success("Teleconsultation settings saved");
    } catch { toast.error("Save failed"); } finally { setSavingTele(false); }
  };

  const loadSub = () => {
    if (user?.role !== "doctor") return;
    api.get("/billing/status").then((r) => setSub(r.data)).catch(() => {});
  };

  const cancelSub = async () => {
    setCancelling(true);
    try {
      const r = await api.post("/billing/cancel");
      toast.success(r.data.message || "Subscription cancelled.");
      setConfirmCancel(false);
      loadSub();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel subscription");
    } finally { setCancelling(false); }
  };

  const loadTeam = () => {
    if (user?.role !== "doctor") return;
    api.get("/access/requests").then((r) => setTeam(r.data)).catch(() => {});
  };

  const act = async (assistant_id, action) => {
    await api.post(`/access/${action}`, { assistant_id });
    toast.success(action === "approve" ? "Assistant approved" : "Assistant rejected");
    loadTeam();
  };

  useEffect(() => {
    api.get("/meta").then((r) => { setSpecialties(r.data.specialties); setCountries(r.data.countries || []); setLanguages(r.data.languages || []); }).catch(() => {});
    loadTeam();
    loadSub();
    if (user) setForm({
      full_name: user.full_name || user.name || "",
      qualifications: user.qualifications || "",
      registration_number: user.registration_number || "",
      specialty: user.specialty || "",
      clinic_name: user.clinic_name || "MDvia Care",
      clinic_address: user.clinic_address || "",
      clinic_contact: user.clinic_contact || "",
      contact_phone: user.contact_phone || "",
      contact_whatsapp: user.contact_whatsapp || "",
      contact_email: user.contact_email || user.email || "",
      country: user.country || "India",
      ui_language: user.ui_language || "English",
    });
    if (user) {
      setTele({
        teleconsult_enabled: !!user.teleconsult_enabled,
        consultation_fee: user.consultation_fee || "",
        upi_id: user.upi_id || "",
        bank_holder: user.bank_holder || "",
        bank_account_number: user.bank_account_number || "",
        bank_ifsc: user.bank_ifsc || "",
        bank_name: user.bank_name || "",
        slot_minutes: user.slot_minutes || 15,
      });
      setAvail(user.availability || {});
    }
  }, [user]);

  const save = async () => {
    if (!form.registration_number || !form.specialty) { toast.error("Registration number and specialty are required"); return; }
    setSaving(true);
    try {
      await api.put("/profile", form);
      await setLanguage(form.ui_language || "English");
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const isGp = (form.specialty || "").toLowerCase().includes("mbbs") || (form.specialty || "").toLowerCase().includes("general physician");

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="profile-tab">
      <div className="rounded-2xl bg-gradient-to-r from-[#EBF3FB] to-[#FFFBEB] border border-blue-100 p-5 flex items-center gap-4">
        {user?.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-2xl border-2 border-white shadow" /> : <div className="w-16 h-16 rounded-2xl bg-[#1E5AA8] text-amber-300 flex items-center justify-center text-2xl font-bold">{(form.full_name || "D")[0]}</div>}
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">{form.full_name || "Doctor"}</h2>
          <p className="text-sm text-slate-500">{user?.email}</p>
          {form.specialty && <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${isGp ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>{isGp ? "GP-level prescribing" : "Specialist-level prescribing"}</span>}
        </div>
      </div>

      {user?.role === "doctor" && sub && (
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6" data-testid="subscription-section">
          <div className="flex items-center gap-2 mb-4"><CreditCard className="w-5 h-5 text-[#1E5AA8]" /><h3 className="font-semibold text-slate-800">Subscription</h3></div>
          {sub.is_admin ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-800" data-testid="sub-admin">Complimentary owner / admin access — all features are unlocked.</div>
          ) : sub.premium ? (
            <>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-slate-100 p-4">
                  <p className="text-xs text-slate-400">Current plan</p>
                  <p data-testid="sub-plan" className="text-lg font-bold text-[#123C73] mt-0.5">{PLAN_LABEL[sub.plan] || "—"}</p>
                </div>
                <div className="rounded-xl border border-slate-100 p-4">
                  <p className="text-xs text-slate-400">Status</p>
                  <span data-testid="sub-status" className={`inline-flex items-center gap-1 mt-1.5 text-xs font-semibold px-2 py-1 rounded-full ${sub.on_trial ? "bg-sky-50 text-sky-700" : sub.cancelled ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                    {sub.on_trial ? "Free trial" : sub.cancelled ? "Cancelling at period end" : "Active"}
                  </span>
                </div>
                <div className="rounded-xl border border-slate-100 p-4">
                  <p className="text-xs text-slate-400">{sub.on_trial ? "Trial ends" : sub.cancelled ? "Access until" : "Renews on"}</p>
                  <p data-testid="sub-renews" className="text-sm font-semibold text-slate-800 mt-1.5 flex items-center gap-1"><CalendarClock className="w-4 h-4 text-[#F59E0B]" />{fmtDate(sub.expires_at)}</p>
                </div>
              </div>
              {sub.on_trial && <p className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-3" data-testid="trial-banner">You're on a 14-day free trial. Subscribe before it ends to keep access — you can do this from the paywall once the trial finishes.</p>}
              {!sub.cancelled && !sub.on_trial && (confirmCancel ? (
                <div className="mt-4 rounded-xl bg-red-50 border border-red-100 p-4">
                  <p className="text-sm text-red-800">Cancel your subscription? Autopay will stop and you keep full access until {fmtDate(sub.expires_at)}.</p>
                  <div className="flex gap-2 mt-3">
                    <button data-testid="confirm-cancel-sub" onClick={cancelSub} disabled={cancelling} className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg disabled:opacity-60">{cancelling && <Loader2 className="w-4 h-4 animate-spin" />} Yes, cancel</button>
                    <button data-testid="keep-sub" onClick={() => setConfirmCancel(false)} className="text-sm text-slate-500 px-4 py-2 rounded-lg hover:bg-white">Keep subscription</button>
                  </div>
                </div>
              ) : (
                <button data-testid="btn-cancel-sub" onClick={() => setConfirmCancel(true)} className="mt-4 text-sm font-semibold text-red-600 hover:text-red-700 hover:underline">Cancel subscription</button>
              ))}
              {sub.cancelled && <p className="mt-4 text-xs text-slate-400">Your plan won't renew. You can subscribe again anytime after it expires.</p>}
            </>
          ) : (
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-800" data-testid="sub-none">No active subscription.</div>
          )}
        </div>
      )}

      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5"><Stethoscope className="w-5 h-5 text-[#1E5AA8]" /><h3 className="font-semibold text-slate-800">Doctor Credentials</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full Name" span2><input data-testid="input-doctor-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inp} placeholder="Dr. Aarav Sharma" /></Field>
          <Field label="Qualifications" span2><input data-testid="input-doctor-qualifications" value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} className={inp} placeholder="MBBS, MD (General Medicine)" /></Field>
          <Field label="Medical Council Registration No. *"><input data-testid="input-doctor-regno" value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} className={inp} placeholder="MCI/NMC-2018-84729" /></Field>
          <Field label="Specialty *">
            <select data-testid="select-doctor-specialty" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className={inp}>
              <option value="">Select specialty…</option>
              {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>

        <div className="flex items-center gap-2 mt-6 mb-4 pt-4 border-t border-slate-100"><Building2 className="w-5 h-5 text-[#F59E0B]" /><h3 className="font-semibold text-slate-800">Clinic Details</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Clinic Name" span2><input data-testid="input-clinic-name" value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} className={inp} /></Field>
          <Field label="Clinic Address" span2><input value={form.clinic_address} onChange={(e) => setForm({ ...form, clinic_address: e.target.value })} className={inp} placeholder="123 Wellness Road, Mumbai" /></Field>
          <Field label="Contact" span2><input value={form.clinic_contact} onChange={(e) => setForm({ ...form, clinic_contact: e.target.value })} className={inp} placeholder="Phone / Email" /></Field>
        </div>

        <div className="flex items-center gap-2 mt-6 mb-4 pt-4 border-t border-slate-100"><Globe className="w-5 h-5 text-[#1E5AA8]" /><h3 className="font-semibold text-slate-800">Region & Language</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Country (drives clinical guidelines) *">
            <SearchableSelect testId="select-country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} options={countries} placeholder="Select country…" />
          </Field>
          <Field label="Interface & Prescription Language">
            <SearchableSelect testId="select-ui-language" value={form.ui_language} onChange={(v) => setForm({ ...form, ui_language: v })} options={languages} placeholder="Select language…" />
          </Field>
        </div>
        <p className="mt-2 text-xs text-slate-400">Treatment, diagnostic tests and counseling follow your country's clinical practice. Recordings are transcribed and prescriptions written in your chosen language (English for India).</p>

        <div className="flex items-center gap-2 mt-6 mb-4 pt-4 border-t border-slate-100"><Share2 className="w-5 h-5 text-[#F59E0B]" /><h3 className="font-semibold text-slate-800">Prescription Sharing — Your Contact</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Your Phone (with country code)"><input data-testid="input-contact-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inp} placeholder="+91 98765 43210" /></Field>
          <Field label="Your WhatsApp (if different)"><input data-testid="input-contact-whatsapp" value={form.contact_whatsapp} onChange={(e) => setForm({ ...form, contact_whatsapp: e.target.value })} className={inp} placeholder="+91 98765 43210" /></Field>
          <Field label="Your Email (shown to patients)" span2>
            <input data-testid="input-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inp} placeholder="clinic@example.com" />
            {form.contact_email && user?.email && form.contact_email.trim().toLowerCase() === user.email.toLowerCase() && (
              <span className="inline-flex items-center gap-1 mt-1.5 text-[11px] text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> Verified via your Google sign-in</span>
            )}
          </Field>
        </div>
        <p className="mt-2 text-xs text-slate-400">When you tap “Share to Patient”, MDvia Care opens your own WhatsApp / SMS / Email with the prescription link ready to send — so patients see it coming from you. These details also appear on the message.</p>

        <div className="flex items-center gap-2 mt-6 mb-4 pt-4 border-t border-slate-100"><PenLine className="w-5 h-5 text-[#1E5AA8]" /><h3 className="font-semibold text-slate-800">Signature & Stamp</h3></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <ImageUpload label="Doctor Signature" kind="signature" testId="upload-signature" image={user?.signature_image} onDone={refresh} />
          <ImageUpload label="Clinic Stamp / Seal" kind="stamp" testId="upload-stamp" image={user?.stamp_image} onDone={refresh} />
        </div>
        <p className="mt-2 text-xs text-slate-400">Uploaded signature & stamp are automatically embedded on every OPD case paper and certificate you download or print.</p>

        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 p-3 flex items-start gap-2">
          <BadgeInfo className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">Your specialty controls which formulary you see. <b>MBBS / General Physician</b> shows GP-level medicines & tests only; specialists get their full prescribing set.</p>
        </div>

        <button data-testid="btn-save-doctor-profile" onClick={save} disabled={saving} className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1E5AA8] text-white font-semibold hover:bg-[#123C73] disabled:opacity-60">
          <Save className="w-5 h-5" /> {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>

      {user?.role === "doctor" && (
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6" data-testid="teleconsult-section">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2"><Video className="w-5 h-5 text-[#1E5AA8]" /><h3 className="font-semibold text-slate-800">Online Teleconsultation</h3></div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <span className="text-xs text-slate-500">{tele.teleconsult_enabled ? "Accepting patients" : "Off"}</span>
              <input data-testid="toggle-teleconsult" type="checkbox" checked={tele.teleconsult_enabled} onChange={(e) => setTele({ ...tele, teleconsult_enabled: e.target.checked })} className="sr-only peer" />
              <span className="relative w-11 h-6 bg-slate-200 rounded-full peer-checked:bg-[#1E5AA8] transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-all peer-checked:after:translate-x-5" />
            </label>
          </div>
          <p className="text-xs text-slate-400 mb-4">Turn this on to appear in the patient app. Patients can find you by specialty, book an available slot and consult you over secure video.</p>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Consultation fee (₹)">
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input data-testid="input-consult-fee" type="number" min="0" value={tele.consultation_fee} onChange={(e) => setTele({ ...tele, consultation_fee: e.target.value })} className={inp + " pl-9"} placeholder="500" />
              </div>
            </Field>
            <Field label="Slot duration">
              <select data-testid="select-slot-minutes" value={tele.slot_minutes} onChange={(e) => setTele({ ...tele, slot_minutes: e.target.value })} className={inp}>
                {[10, 15, 20, 30, 45, 60].map((m) => <option key={m} value={m}>{m} minutes</option>)}
              </select>
            </Field>
          </div>

          <div className="flex items-center gap-2 mt-6 mb-3 pt-4 border-t border-slate-100"><Clock className="w-5 h-5 text-[#F59E0B]" /><h4 className="font-semibold text-slate-800 text-sm">Weekly availability</h4></div>
          <div className="space-y-2" data-testid="availability-editor">
            {DAYS.map(([d, label]) => {
              const day = avail[d] || { enabled: false, start: "18:00", end: "21:00" };
              return (
                <div key={d} className="flex items-center gap-3 flex-wrap">
                  <label className="inline-flex items-center gap-2 w-24 shrink-0">
                    <input data-testid={`avail-${d}-enabled`} type="checkbox" checked={!!day.enabled} onChange={(e) => setDay(d, { enabled: e.target.checked })} className="w-4 h-4 accent-[#1E5AA8]" />
                    <span className="text-sm text-slate-700">{label}</span>
                  </label>
                  <input data-testid={`avail-${d}-start`} type="time" value={day.start || "18:00"} disabled={!day.enabled} onChange={(e) => setDay(d, { start: e.target.value })} className={`${inp} w-32 disabled:opacity-40`} />
                  <span className="text-slate-400 text-sm">to</span>
                  <input data-testid={`avail-${d}-end`} type="time" value={day.end || "21:00"} disabled={!day.enabled} onChange={(e) => setDay(d, { end: e.target.value })} className={`${inp} w-32 disabled:opacity-40`} />
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2 mt-6 mb-3 pt-4 border-t border-slate-100"><CreditCard className="w-5 h-5 text-[#1E5AA8]" /><h4 className="font-semibold text-slate-800 text-sm">Get paid by patients (UPI / GPay)</h4></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your UPI ID (e.g. name@okhdfcbank)"><input data-testid="input-upi-id" value={tele.upi_id} onChange={(e) => setTele({ ...tele, upi_id: e.target.value })} className={inp} placeholder="doctor@upi" /></Field>
            <div className="sm:col-span-1">
              <ImageUpload label="GPay / UPI QR image (optional)" kind="upi_qr" testId="upload-upi-qr" image={user?.upi_qr_image} onDone={refresh} />
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400">Patients scan this to pay you directly — the money goes straight to your account. MDvia Care does not take a cut.</p>

          <details className="mt-4">
            <summary className="text-sm text-slate-500 cursor-pointer inline-flex items-center gap-2"><Landmark className="w-4 h-4" /> Add bank account (optional)</summary>
            <div className="grid sm:grid-cols-2 gap-4 mt-3">
              <Field label="Account holder name"><input data-testid="input-bank-holder" value={tele.bank_holder} onChange={(e) => setTele({ ...tele, bank_holder: e.target.value })} className={inp} /></Field>
              <Field label="Bank name"><input data-testid="input-bank-name" value={tele.bank_name} onChange={(e) => setTele({ ...tele, bank_name: e.target.value })} className={inp} /></Field>
              <Field label="Account number"><input data-testid="input-bank-account" value={tele.bank_account_number} onChange={(e) => setTele({ ...tele, bank_account_number: e.target.value })} className={inp} /></Field>
              <Field label="IFSC code"><input data-testid="input-bank-ifsc" value={tele.bank_ifsc} onChange={(e) => setTele({ ...tele, bank_ifsc: e.target.value })} className={inp} /></Field>
            </div>
          </details>

          <button data-testid="btn-save-teleconsult" onClick={saveTele} disabled={savingTele} className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1E5AA8] text-white font-semibold hover:bg-[#123C73] disabled:opacity-60">
            {savingTele ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save teleconsultation settings
          </button>
        </div>
      )}

      {user?.role === "doctor" && (
        <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6" data-testid="team-section">
          <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-[#1E5AA8]" /><h3 className="font-semibold text-slate-800">Team — Doctor's Assistants</h3></div>
          <p className="text-xs text-slate-400 mb-4">Approve assistants who requested access. Approved assistants can book & manage your appointments, and their changes appear on your account live.</p>
          {team.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No assistant requests yet. Share your email ({user?.email}) with your assistant so they can request access.</p>}
          <div className="space-y-2">
            {team.map((r) => (
              <div key={r.assistant_id} data-testid={`team-row-${r.assistant_id}`} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border border-slate-100">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800" data-notranslate>{r.assistant_name || "Assistant"}</p>
                  <p className="text-xs text-slate-400" data-notranslate>{r.assistant_email}</p>
                </div>
                {r.status === "pending" ? (
                  <div className="flex gap-2">
                    <button data-testid={`approve-${r.assistant_id}`} onClick={() => act(r.assistant_id, "approve")} className="inline-flex items-center gap-1 text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
                    <button data-testid={`reject-${r.assistant_id}`} onClick={() => act(r.assistant_id, "reject")} className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-600 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-100"><XCircle className="w-3.5 h-3.5" /> Reject</button>
                  </div>
                ) : (
                  <span className={`text-xs px-2 py-1 rounded-full border capitalize ${r.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`}>{r.status}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, span2, children }) {
  return <div className={span2 ? "sm:col-span-2" : ""}><label className="text-xs font-medium text-slate-500 mb-1 block">{label}</label>{children}</div>;
}

function ImageUpload({ label, kind, testId, image, onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/profile/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onDone();
      toast.success(`${label} uploaded`);
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  const clear = async () => { setBusy(true); try { await api.delete(`/profile/${kind}`); await onDone(); } finally { setBusy(false); } };
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <label className="text-xs font-medium text-slate-500 mb-2 block">{label}</label>
      <div className="flex items-center gap-3">
        <div className="w-28 h-16 rounded-lg bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center overflow-hidden">
          {image ? <img src={image} alt={label} className="max-h-full max-w-full object-contain" /> : <span className="text-[11px] text-slate-400">No image</span>}
        </div>
        <div className="flex flex-col gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1E5AA8] cursor-pointer hover:underline">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload PNG/JPG
            <input data-testid={testId} type="file" accept="image/*" className="hidden" onChange={upload} />
          </label>
          {image && <button onClick={clear} className="inline-flex items-center gap-1.5 text-xs text-red-500 hover:underline"><Trash2 className="w-3.5 h-3.5" /> Remove</button>}
        </div>
      </div>
    </div>
  );
}
